import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuColumns3, LuEye, LuRotateCcw } from 'react-icons/lu';
import Button from '../ui/Button';
import Popover from '../ui/Popover';
import { useTranslation } from '../../i18n/LanguageContext';

/**
 * "Sütunlar" button: which table columns are shown. `autoHidden` marks
 * columns the page hides by itself right now ({ key: { label, hint } }).
 */
const ColumnMenu = ({ columns, visibleColumns, autoHidden = {}, onToggle, onShowAll, onReset }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const total = columns.length;
  const visibleCount = columns.filter((column) => visibleColumns[column.key]).length;

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('dataTable.columns.buttonLabel', { count: visibleCount, total })}
      >
        <LuColumns3 aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-500 dark:tw-text-slate-400" />
        <span className="tw-hidden sm:tw-inline">{t('dataTable.columns.button')}</span>
        <span className="tw-rounded-full tw-bg-slate-900/[0.06] tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-300">
          {visibleCount}/{total}
        </span>
      </Button>

      <Popover
        anchorRef={buttonRef}
        open={open}
        onClose={() => setOpen(false)}
        align="end"
        role="menu"
        label={t('dataTable.columns.title')}
        className="tw-w-[28rem] tw-p-2"
      >
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-px-2.5 tw-pb-2 tw-pt-1.5">
          <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t('dataTable.columns.title')}</p>
          <p className="tw-text-xs tw-font-medium tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
            {t('dataTable.columns.visible', { count: visibleCount, total })}
          </p>
        </div>

        <div className="tw-grid tw-max-h-[min(22rem,60vh)] tw-gap-0.5 tw-overflow-y-auto tw-overscroll-contain glass-scrollbar sm:tw-grid-cols-2">
          {columns.map(({ key, label }) => {
            const checked = Boolean(visibleColumns[key]);
            const isLastVisible = checked && visibleCount <= 1;
            const auto = checked ? autoHidden[key] : null;
            return (
              <button
                key={key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                disabled={isLastVisible}
                title={isLastVisible ? t('dataTable.columns.lastColumn') : undefined}
                onClick={() => onToggle(key)}
                className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-transparent tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-text-slate-700 tw-transition-colors hover:tw-bg-slate-900/[0.05] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.07]"
              >
                <span
                  aria-hidden="true"
                  className={`tw-grid tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-place-items-center tw-rounded-md tw-border tw-transition-colors ${
                    checked
                      ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white dark:tw-border-teal-400 dark:tw-bg-teal-500'
                      : 'tw-border-slate-300 tw-bg-white/60 dark:tw-border-white/20 dark:tw-bg-white/[0.04]'
                  }`}
                >
                  {checked && <LuCheck className="tw-h-3 tw-w-3" strokeWidth={3} />}
                </span>
                <span className="tw-min-w-0 tw-flex-1 tw-truncate">{label}</span>
                {auto && (
                  <span
                    title={auto.hint}
                    className="tw-shrink-0 tw-rounded-full tw-bg-slate-900/[0.06] tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-slate-500 dark:tw-bg-white/10 dark:tw-text-slate-400"
                  >
                    {auto.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-border-t tw-border-slate-200/70 tw-px-1 tw-pt-2 dark:tw-border-white/10">
          <Button variant="ghost" size="sm" onClick={onShowAll}>
            <LuEye aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {t('dataTable.columns.showAll')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onReset} title={t('dataTable.columns.resetHint')}>
            <LuRotateCcw aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {t('dataTable.columns.reset')}
          </Button>
        </div>
      </Popover>
    </>
  );
};

ColumnMenu.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  visibleColumns: PropTypes.objectOf(PropTypes.bool).isRequired,
  autoHidden: PropTypes.objectOf(PropTypes.shape({ label: PropTypes.string, hint: PropTypes.string })),
  onToggle: PropTypes.func.isRequired,
  onShowAll: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default ColumnMenu;
